// designeeSelfMatch — WEL-139. The household designee has to be a THIRD PARTY:
// the person we contact about a resident's belongings if the resident can't be
// reached. Naming yourself defeats the purpose.
//
// Miles's call, and the reason this WARNS instead of blocking: "If the name
// matches that doesn't 100% mean that it is them as a parent could have the same
// name. So it should detect the match then flag/warn to the filler to ensure
// they aren't putting themself."
//
// So: detect confidently, say so plainly, and let them proceed. A hard block
// would be wrong for a genuine Jr./Sr. household — and a resident stuck behind a
// validation error on a form they can't argue with is worse than a warning they
// can read and dismiss.
//
// Pure (no `./api`, no JSX) so it stays unit-testable under node:test/tsx.

/** Trim, lowercase, collapse whitespace, drop punctuation used in names. */
function normalize(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[.,]/g, '')
    .replace(/\s+/g, ' ');
}

/** First and last token — the parts that identify someone across middle names. */
function firstLast(name: string): string {
  const parts = normalize(name).split(' ').filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0]!;
  return `${parts[0]} ${parts[parts.length - 1]}`;
}

/**
 * Does the designee name look like the person filling the form?
 *
 * Matches on the whole normalized name, or on first+last so a middle name or
 * initial on one side doesn't hide it ("Sydney Grey" vs "Sydney M. Grey").
 *
 * Deliberately NOT fuzzy: nicknames, initials-only and misspellings are left
 * alone. This warning has to be right nearly every time it fires — a resident
 * who gets told "that looks like you" about their actual brother learns to
 * ignore the banner, and then it protects nobody.
 *
 * Returns false when either side is blank (nothing to compare yet).
 */
export function isLikelySelfDesignation(
  designeeName: string | undefined,
  fillerName: string | undefined,
): boolean {
  const designee = normalize(designeeName ?? '');
  const filler = normalize(fillerName ?? '');
  if (!designee || !filler) return false;
  if (designee === filler) return true;
  const dFL = firstLast(designee);
  const fFL = firstLast(filler);
  // A single-token name ("Cher") is too weak a signal to match on its own.
  if (!dFL.includes(' ') || !fFL.includes(' ')) return false;
  return dFL === fFL;
}
